import React from "react";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState, EmptyState, ErrorState } from "@/components/v3/States";

const Swatch = ({ hex, name, usage }) => (
  <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
    <div className="h-14" style={{ background: hex }} />
    <div className="p-3">
      <div className="text-sm font-semibold text-slate-800">{name}</div>
      <div className="text-[11px] text-slate-400 tabular-nums">{hex}</div>
      <div className="text-[11px] text-slate-500 mt-1">{usage}</div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg font-semibold text-slate-900 mb-1">{title}</h2>
    <div className="h-px bg-slate-200 mb-5" />
    {children}
  </section>
);

export default function StyleGuide() {
  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="styleguide-page">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#b3122a]">Design System</div>
        <h1 className="font-['Playfair_Display'] text-4xl text-slate-900 mt-1">Red Fort Capital Loan Pulse Style Guide</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Institutional, high-information-density, evidence-driven. Red is a controlled accent — never decoration.
          Desktop-first for internal screens; DSA and application flows are mobile-responsive.
        </p>
      </div>

      <Section title="Colour">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Swatch hex="#b3122a" name="Brand Red" usage="Primary CTA, active state, critical alert. Controlled accent only." />
          <Swatch hex="#111111" name="Ink Navy" usage="Sidebar, dark surfaces." />
          <Swatch hex="#F9FAFB" name="Base" usage="Page background." />
          <Swatch hex="#FFFFFF" name="Surface" usage="Cards, always with 1px slate-200 border." />
          <Swatch hex="#059669" name="Success" usage="Positive deltas, cleared states." />
          <Swatch hex="#EA580C" name="Warning" usage="SLA at risk, ageing." />
          <Swatch hex="#DC2626" name="Danger" usage="Overdue, blocked, breach." />
          <Swatch hex="#0284C7" name="Info" usage="Informational chips." />
        </div>
      </Section>

      <Section title="Typography">
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <div>
            <div className="ceo-label">Playfair Display — hero figures & page titles</div>
            <div className="font-['Playfair_Display'] text-4xl text-slate-900 mt-1">₹142.6 Cr</div>
          </div>
          <div>
            <div className="ceo-label">Manrope — UI, labels, body</div>
            <div className="text-sm text-slate-700 mt-1">All interface text, table content and chart labels use Manrope.</div>
          </div>
          <div>
            <div className="ceo-label">Tabular numerals for money</div>
            <div className="text-xl font-semibold tabular-nums tracking-tight text-slate-900 mt-1">1,04,50,000.00 · 18.75% · 61-90 DPD</div>
          </div>
        </div>
      </Section>

      <Section title="Data-source badges">
        <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-wrap items-center gap-4">
          <SourceBadge source="HISTORICAL" />
          <span className="text-xs text-slate-500">LMS — live LMS 1.0 database, read-only. Never editable in HQ.</span>
          <SourceBadge source="TEST_V3" />
          <span className="text-xs text-slate-500" data-testid="styleguide-test-storage">HQ — captured in HQ, not yet in the LMS. Written to isolated HQ overlay collections in the configured application database.</span>
          <SourceBadge source="MOCK" />
          <span className="text-xs text-slate-500">MOCK — simulated provider step (bank feed, eSign, CIBIL, AA, KYC); never claims real verification.</span>
          <SourceBadge source="SNAPSHOT" />
          <span className="text-xs text-slate-500">HISTORICAL — frozen loanbooks monthly snapshot; used only on Portfolio / Closed / historical cockpit views.</span>
        </div>
      </Section>

      <Section title="Buttons & chips">
        <div className="bg-white border border-slate-200 rounded-sm p-6 flex flex-wrap items-center gap-3">
          <button data-testid="sg-btn-primary" className="bg-[#b3122a] text-white text-sm font-semibold rounded px-4 py-2 hover:bg-[#8f0e21] transition-colors">Primary action</button>
          <button className="border border-slate-300 text-slate-700 text-sm font-semibold rounded px-4 py-2 hover:border-slate-500 transition-colors">Secondary</button>
          <button className="text-[#b3122a] text-sm font-semibold underline underline-offset-4">Tertiary link</button>
          <span className="bg-emerald-50 text-emerald-700 text-xs font-bold rounded px-2 py-1">+4.2% MoM</span>
          <span className="bg-red-50 text-red-700 text-xs font-bold rounded px-2 py-1">PAR 30 ↑</span>
          <span className="bg-slate-100 text-slate-600 text-xs font-bold rounded px-2 py-1 uppercase tracking-wider">Stage · Credit</span>
        </div>
      </Section>

      <Section title="States">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm"><LoadingState /></div>
          <div className="bg-white border border-slate-200 rounded-sm"><EmptyState /></div>
          <div className="bg-white border border-slate-200 rounded-sm"><ErrorState message="Sample failure — historical datastore unreachable." onRetry={() => {}} /></div>
        </div>
        <p className="text-xs text-slate-400 mt-3">Ground rule: missing data renders “Not available”. The system never fabricates values.</p>
      </Section>

      <Section title="Table pattern">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-4 py-2.5">Borrower</th><th className="px-4 py-2.5">Amount</th>
                <th className="px-4 py-2.5">Stage</th><th className="px-4 py-2.5">Source</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2.5 font-semibold text-slate-800">Sharma Industries Pvt Ltd</td>
                <td className="px-4 py-2.5 tabular-nums">₹5,00,00,000</td>
                <td className="px-4 py-2.5"><span className="bg-slate-100 text-slate-600 text-xs font-bold rounded px-2 py-0.5">CREDIT</span></td>
                <td className="px-4 py-2.5"><SourceBadge source="TEST_V3" /></td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-semibold text-slate-800">Kothawale Petroleum Services</td>
                <td className="px-4 py-2.5 tabular-nums">₹3,00,00,000</td>
                <td className="px-4 py-2.5"><span className="bg-slate-100 text-slate-600 text-xs font-bold rounded px-2 py-0.5">HIST</span></td>
                <td className="px-4 py-2.5"><SourceBadge source="HISTORICAL" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
